/**
 * Moon tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const moonTattoosPack: ToilePack = {
    slug: 'moon-tattoos',
    category: 'tattoo',
    name: 'Moon tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'crescent', label: 'Crescent and stars', subject: 'a thin crescent moon with three small stars and a scatter of dots around it'},
        {key: 'phases', label: 'Moon phases', subject: 'the phases of the moon in a vertical row, from new to full and back, evenly spaced as if down the spine'},
        {key: 'face', label: 'Moon with face', subject: 'a crescent moon with a calm sleeping face drawn along its inner curve, the eye closed, lashes showing'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a sun', 'blue ink', 'behind ear'],
    directionHint: 'try: a sun',
    seo: {
        title: 'Moon tattoo designs: crescent and stars, moon phases, face',
        metaDescription: 'Nine moon tattoo designs: a crescent with stars, the phases of the moon in a row and a sleeping crescent, in three styles.',
        h1: 'Moon tattoo designs: crescent and stars, moon phases, crescent with a face',
        tagline: 'Three moons, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Moon tattoo designs, small and personal, worn behind the ear, on a finger or the wrist, or larger down the spine. The nine sheets show a thin crescent with stars, the phases of the moon in a vertical row, and a crescent with a sleeping face. Each is drawn as traditional flash, fine line and dotwork. Type a different sky object or twist, such as a sun, and the same styles can be asked for.',
        useCases: [
            'a small crescent behind the ear',
            'a vertical row of moon phases down the spine',
            'a finger or wrist moon',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine moon designs: a crescent with stars, the moon phases in a row and a crescent with a sleeping face, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a sun or a different colour?', a: 'Yes. Type a different sky object or twist, such as a sun or blue ink; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
